import { useQuery } from '@apollo/client';
import _ from 'lodash';
import * as React from 'react';
import { useEffect, useMemo } from 'react';
import { Badge, Button, Card, Form, Table } from 'react-bootstrap';
import { useHistory, useParams } from 'react-router';
import { LoadingLogo } from '../../../devtools/components/Modal';
import { formatUtcDate } from '../RedeemCodes/utils';
import DeepLinkForm from './components/DeepLinkForm';
import { LIVEOPS_DEEP_LINKS_QUERY } from './graphql';
import { DeepLinkItem, formatTarget, getDeepLinkStatus } from './utils';

const baseURL = '/sp3/liveops/deep-links';

const STATUS_FILTERS = ['All', 'Active', 'Disabled', 'Expired'] as const;
type StatusFilter = typeof STATUS_FILTERS[number];

function DeepLinks() {
  const { id: deepLinkId } = useParams<{ id: string }>();
  const history = useHistory();

  const { data, loading: fetchingLoading } = useQuery(LIVEOPS_DEEP_LINKS_QUERY, { fetchPolicy: 'no-cache' });

  const [deepLinks, setDeepLinks] = React.useState<DeepLinkItem[]>([]);
  const [isCreating, setIsCreating] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('All');
  const [copiedId, setCopiedId] = React.useState<string | null>(null);

  useEffect(() => {
    if (data?.liveOps) {
      setDeepLinks(_.orderBy(data.liveOps.deepLinks as DeepLinkItem[], 'created', 'desc'));
    }
  }, [data]);

  // Clean up the raw :id placeholder some navigations leave in the URL
  useEffect(() => {
    if (deepLinkId === ':id') {
      history.replace(baseURL);
    }
  }, [deepLinkId, history]);

  const selectedDeepLink = deepLinks.find(({ id }) => id === deepLinkId);

  const filteredDeepLinks = useMemo(() => {
    const query = search.trim().toLowerCase();
    return deepLinks.filter((deepLink) => {
      if (
        query &&
        ![deepLink.name, deepLink.campaign ?? '', deepLink.id].some((value) => value.toLowerCase().includes(query))
      ) {
        return false;
      }
      return statusFilter === 'All' || getDeepLinkStatus(deepLink).label === statusFilter;
    });
  }, [deepLinks, search, statusFilter]);

  const handleSaved = (deepLink: DeepLinkItem) => {
    const others = deepLinks.filter(({ id }) => id !== deepLink.id);
    setDeepLinks(_.orderBy([...others, deepLink], 'created', 'desc'));
    setIsCreating(false);
    // Stay on the link after creating it so it can be copied right away
    history.push(`${baseURL}/${deepLink.id}`);
  };

  const handleDeleted = (deletedId: string) => {
    setDeepLinks((current) => current.filter(({ id }) => id !== deletedId));
    history.push(baseURL);
  };

  const handleBackToList = () => {
    setIsCreating(false);
    history.push(baseURL);
  };

  const handleCopyLink = (deepLink: DeepLinkItem, event?: React.MouseEvent) => {
    event?.stopPropagation();
    navigator.clipboard
      .writeText(deepLink.url)
      .then(() => {
        setCopiedId(deepLink.id);
        setTimeout(() => setCopiedId(null), 1500);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const renderCopyButton = (deepLink: DeepLinkItem) => (
    <Button variant="outline-primary" size="sm" onClick={(e: React.MouseEvent) => handleCopyLink(deepLink, e)}>
      {copiedId === deepLink.id ? 'Copied!' : 'Copy link'}
    </Button>
  );

  const showDetail = isCreating || !_.isNil(selectedDeepLink);

  return (
    <>
      <LoadingLogo show={fetchingLoading} />

      {showDetail ? (
        <div className="mb-5">
          <Button variant="link" className="pl-0 mb-2" onClick={handleBackToList}>
            <i className="fas fa-chevron-left mr-2" />
            Back to list
          </Button>
          <Card>
            <Card.Body>
              {isCreating ? (
                <>
                  <Card.Title>Create Deeplink</Card.Title>
                  <DeepLinkForm deepLink={null} onSaved={handleSaved} onCancel={handleBackToList} />
                </>
              ) : (
                selectedDeepLink && (
                  <>
                    <Card.Title className="d-flex align-items-center">
                      <span>{selectedDeepLink.name}</span>
                      <Badge className="ml-2" pill variant={getDeepLinkStatus(selectedDeepLink).variant as never}>
                        {getDeepLinkStatus(selectedDeepLink).label}
                      </Badge>
                    </Card.Title>
                    <div className="d-flex align-items-center mb-2" style={{ gap: '0.75rem' }}>
                      <span className="text-monospace">{selectedDeepLink.url}</span>
                      {renderCopyButton(selectedDeepLink)}
                    </div>
                    <div className="text-muted small mb-3">
                      Opened by {selectedDeepLink.stats.uniqueUsers} players ({selectedDeepLink.stats.totalOpens} opens)
                      · Created: {formatUtcDate(selectedDeepLink.created)} UTC · Updated:{' '}
                      {formatUtcDate(selectedDeepLink.updatedAt)} UTC
                    </div>
                    <DeepLinkForm deepLink={selectedDeepLink} onSaved={handleSaved} onDeleted={handleDeleted} />
                  </>
                )
              )}
            </Card.Body>
          </Card>
        </div>
      ) : (
        <div className="mb-5">
          <div className="d-flex align-items-center mb-3" style={{ gap: '0.75rem' }}>
            <Form.Control
              type="text"
              placeholder="Search name, campaign or ID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ maxWidth: 280 }}
            />
            <div>
              {STATUS_FILTERS.map((filter) => (
                <Button
                  key={filter}
                  size="sm"
                  className="rounded-pill mr-1"
                  variant={statusFilter === filter ? 'info' : 'outline-secondary'}
                  onClick={() => setStatusFilter(filter)}
                >
                  {filter}
                </Button>
              ))}
            </div>
            <Button variant="success" className="ml-auto" onClick={() => setIsCreating(true)}>
              <i className="fas fa-plus mr-2" />
              create Deeplink
            </Button>
          </div>

          <Card>
            <Card.Body className="p-0">
              {filteredDeepLinks.length > 0 ? (
                <Table hover responsive size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th className="pl-3">Name</th>
                      <th>Status</th>
                      <th>Screen</th>
                      <th>Link</th>
                      <th className="text-right">Players</th>
                      <th className="text-right">Opens</th>
                      <th>Expires (UTC)</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDeepLinks.map((deepLink) => {
                      const status = getDeepLinkStatus(deepLink);
                      return (
                        <tr
                          key={deepLink.id}
                          style={{ cursor: 'pointer' }}
                          onClick={() => history.push(`${baseURL}/${deepLink.id}`)}
                        >
                          <td className="pl-3 align-middle">
                            <div className="font-weight-bold">{deepLink.name}</div>
                            {deepLink.campaign && <div className="text-muted small">{deepLink.campaign}</div>}
                          </td>
                          <td className="align-middle">
                            <Badge pill variant={status.variant as never}>
                              {status.label}
                            </Badge>
                          </td>
                          <td className="align-middle">{formatTarget(deepLink)}</td>
                          <td className="align-middle text-nowrap">
                            <div className="d-flex align-items-center" style={{ gap: '0.75rem' }}>
                              <span className="text-muted small text-monospace">{deepLink.url}</span>
                              {renderCopyButton(deepLink)}
                            </div>
                          </td>
                          <td className="align-middle text-right">
                            {deepLink.stats.uniqueUsers}
                            {deepLink.maxUses ? ` / ${deepLink.maxUses}` : ''}
                          </td>
                          <td className="align-middle text-right">{deepLink.stats.totalOpens}</td>
                          <td className="align-middle text-nowrap">
                            {deepLink.endDate ? formatUtcDate(deepLink.endDate) : '∞'}
                          </td>
                          <td className="align-middle text-muted">
                            <i className="fas fa-chevron-right" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              ) : (
                !fetchingLoading && (
                  <div className="text-center text-muted py-5">
                    {deepLinks.length === 0 ? (
                      <>
                        <p className="mb-3">No deeplinks yet.</p>
                        <Button variant="success" onClick={() => setIsCreating(true)}>
                          <i className="fas fa-plus mr-2" />
                          create Deeplink
                        </Button>
                      </>
                    ) : (
                      <p className="mb-0">No deeplinks match the current search/filter.</p>
                    )}
                  </div>
                )
              )}
            </Card.Body>
          </Card>
        </div>
      )}
    </>
  );
}

export default DeepLinks;
